import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BackrsFormValues, handleLoggingIn, lottieAnimations } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useFetchOnboardingQuestionsQuery } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { handleAfterLogin } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { Box, SxProps, Theme } from '@mui/material';
import Lottie from 'lottie-react';

import { usePostData } from './hooks/usePostData';
import { InitialQuestion } from './InitialQuestion';
import { ONBOARDING_CONFIGS, OnboardingFlow } from './onboarding.types';
import { OnboardingPage } from './OnboardingPage';

const isOnboardingTypeValid = (onboardingType?: string): onboardingType is OnboardingFlow => {
  if (!onboardingType) {
    return false;
  }

  return onboardingType in ONBOARDING_CONFIGS;
};

export const OnboardingPageContainer = () => {
  const { onboardingType } = useParams<{ onboardingType: string }>();

  const [searchParams] = useSearchParams();
  const at = searchParams.get('at');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.user);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isShowingAccountCreationLoading, setIsShowingAccountCreationLoading] = useState(false);
  const [isViewingRecommendations, setIsViewingRecommendations] = useState(
    at === 'recommendations',
  );

  const {
    data: questions,
    isLoading,
    error,
  } = useFetchOnboardingQuestionsQuery(onboardingType as OnboardingFlow);

  const careerPathCategories = useAppSelector((state) => state.careerPath.careerCategories);

  useEffect(() => {
    if (error || !isOnboardingTypeValid(onboardingType)) {
      // if we happen to land at a route that isn't an onboarding route, we should redirect to the initial onboarding page and have the user select a flow
      navigate(APP_ROUTE.ONBOARDING);
    }

    if (user?.access_token) {
      navigate(APP_ROUTE.HOME);
    }
  }, [error, navigate, onboardingType, user?.access_token]);

  const questionKeys = questions ? Object.keys(questions) : [];
  const currentQuestion = questions ? questions[questionKeys[currentQuestionIndex]] : undefined;

  const {
    handleCreateUser,
    handlePostProfileOptionAnswers,
    handlePostUserActivity,
    handlePostProfileOption,
  } = usePostData({
    questions,
    careerPathCategories,
  });

  const goToNextQuestion = async (formData: BackrsFormValues) => {
    if (currentQuestionIndex < questionKeys.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);

      return;
    }

    setIsShowingAccountCreationLoading(true);

    try {
      const user = await handleCreateUser(formData);

      await Promise.all([
        handlePostProfileOptionAnswers(formData, user),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);

      await handlePostProfileOption(formData, user, questions);

      await handleLoggingIn({
        phone: '',
        email: formData['email'],
        password: formData['password'],
        environmentUrl: ENVIRONMENT_URL,
        afterLogin: async () => {
          // first we update the user activity endpoint
          await handlePostUserActivity(user);

          // then we set the user's access token
          await handleAfterLogin({
            access_token: user.access_token,
            dispatch,
            setErrorMessage: () => null,
          });

          setIsShowingAccountCreationLoading(false);
          setIsViewingRecommendations(true);

          navigate(`${APP_ROUTE.ONBOARDING}/${onboardingType}`);
        },
        setErrorMessage: () => null,
      });
    } catch (error) {
      setIsShowingAccountCreationLoading(false);
      throw error;
    }

    setIsShowingAccountCreationLoading(false);
  };

  const selectFlow = (onboardingType: OnboardingFlow) => {
    navigate(`${APP_ROUTE.ONBOARDING}/${onboardingType}`);
  };

  const resetQuestionIndex = () => {
    setCurrentQuestionIndex(0);
  };

  if (isLoading) {
    return (
      <Box sx={loadingContainerStyle}>
        <Lottie animationData={lottieAnimations.dotsDotting} />
      </Box>
    );
  }

  if ((!questions || !onboardingType) && !user?.access_token) {
    return <InitialQuestion onComplete={selectFlow} resetOnboarding={resetQuestionIndex} />;
  }

  return (
    <OnboardingPage
      currentFlow={onboardingType as OnboardingFlow}
      currentQuestion={currentQuestion}
      goToNextQuestion={goToNextQuestion}
      questionKeys={questionKeys}
      currentQuestionIndex={currentQuestionIndex}
      setCurrentQuestionIndex={setCurrentQuestionIndex}
      isShowingAccountCreationLoading={isShowingAccountCreationLoading}
      isViewingRecommendations={isViewingRecommendations}
    />
  );
};

const loadingContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100dvh',
};
